import { FEEDBACK_STANCES } from '../../domain/constants/auditResults';

/** @typedef {import('../../domain/entities/Audit.types').AuditVerdict} AuditVerdict */
/** @typedef {import('../../domain/entities/Audit.types').AuditFeedbackStance} AuditFeedbackStance */
/** @typedef {import('../../domain/entities/AuditLearning.types').FeedbackLearningOutcome} FeedbackLearningOutcome */

/**
 * Creates the use case for submitting feedback on an audit result, including marking it as accurate or inaccurate,
 * and removing the audit result.
 * @param {Function} submitAuditFeedback
 * @param {Function} removeAuditFromSessionActivity
 * @returns {{ markAccurate: Function, markInaccurate: Function, removeAuditResult: Function }}

 */
// eslint-disable-next-line import/prefer-default-export
export function createAuditFeedback(submitAuditFeedback, removeAuditFromSessionActivity) {
  /**
   * Sends feedback for the audit result, including the user's stance, comment, and corrected verdict.
   * @param {object} sessionActivity - The session activity object containing the audit result.
   * @param {AuditFeedbackStance} stance - The user's stance on the audit result ('accurate' or 'inaccurate').
   * @param {string|null} comment - The user's comment providing additional context or explanation.
   * @param {AuditVerdict|null} correctedVerdict - The user's corrected verdict if the audit result is inaccurate.
   * @returns {Promise<FeedbackLearningOutcome|null>} - The proposed learning outcome if the feedback results
   * in a learning proposal, otherwise null.
   */
  async function sendFeedback(sessionActivity, stance, comment, correctedVerdict) {
    const { audit } = sessionActivity;
    if (!audit) {
      throw new Error('No audit found for session activity');
    }
    return submitAuditFeedback({
      clinicId: sessionActivity.clinic,
      sessionActivityAuditId: audit.sessionActivityAuditId,
      stance,
      comment,
      correctedVerdict,
    });
  }

  /**
   * Marks the audit result as accurate by submitting feedback with an 'accurate' stance.
   * @param {object} sessionActivity - The session activity object containing the audit result.
   */
  async function markAccurate(sessionActivity) {
    await sendFeedback(sessionActivity, FEEDBACK_STANCES.ACCURATE, null, null);
  }

  /**
   * Marks the audit result as inaccurate by submitting feedback with an 'inaccurate' stance.
   * @param {object} sessionActivity - The session activity object containing the audit result.
   * @param {string|null} comment - The user's comment providing additional context or explanation.
   * @param {AuditVerdict|null} correctedVerdict - The user's corrected verdict if the audit result is inaccurate.
   * @returns {Promise<FeedbackLearningOutcome|null>} - The proposed learning outcome if the feedback results
   * in a learning proposal, otherwise null.
   */
  async function markInaccurate(sessionActivity, comment, correctedVerdict) {
    return sendFeedback(sessionActivity, FEEDBACK_STANCES.INACCURATE, comment, correctedVerdict);
  }

  /**
   * Removes the audit result from the session activity, effectively clearing the audit. This does not delete the
   * logged audit or feedback, but simply disassociates it from the session activity so it no longer appears in the UI.
   * @param {object} sessionActivity - The session activity object containing the audit result.
   */
  async function removeAuditResult(sessionActivity) {
    await removeAuditFromSessionActivity(sessionActivity.id);
  }

  return { markAccurate, markInaccurate, removeAuditResult };
}
